import { useLocalization } from '@ais-ui/locale-management';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@ais-ui/ui-core';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { cn } from '@ais-ui/utils';
import { ReportsFormValues } from './useReportsForm';

interface ReportFormProps {
  form: UseFormReturn<ReportsFormValues>;
}

export const ReportForm = ({ form }: ReportFormProps) => {
  const { messages } = useLocalization();

  return (
    <Form {...form}>
      <div className="space-y-4">
        <FormField
          key="show_boundary"
          control={form.control}
          name="show_boundary"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {messages.settingsReportShowBoundaryLabel}
                </FormLabel>
                <FormDescription>
                  {messages.settingsReportShowBoundaryDescription}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          key="show_job_details_in_export"
          control={form.control}
          name="show_job_details_in_export"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <div className="flex items-center space-x-2">
                  <FormLabel className="text-base">
                    {messages.settingsReportShowJobDetailsExportLabel}
                  </FormLabel>
                  <InfoIconTooltip>
                    {messages.settingsReportShowJobDetailsInfoIconLabel}
                  </InfoIconTooltip>
                </div>
                <FormDescription>
                  {messages.settingsReportShowJobDetailsExportDescription}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          key="export_all_rows_in_report"
          control={form.control}
          name="export_all_rows_in_report"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {messages.settingsReportExportAllRowsLabel}
                </FormLabel>
                <FormDescription>
                  {messages.settingsReportExportAllRowsDescription}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          key="interpret_ship_to_be_stopped_below_speed"
          control={form.control}
          name="interpret_ship_to_be_stopped_below_speed"
          render={({ field }) => (
            <FormItem
              className={cn(
                'flex flex-row items-center justify-between border rounded-lg p-4'
              )}
            >
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {messages.settingsReportInterpretShipToBeStoppedLabel}
                </FormLabel>
                <FormDescription>
                  {messages.settingsReportInterpretShipToBeStoppedDescription}{' '}
                </FormDescription>
              </div>
              <FormControl
                id="settings-message-lookback"
                onChange={() => form.clearErrors()}
              >
                <Input
                  className="w-20"
                  type="number"
                  {...field}
                  value={field.value ?? ''} // Ensure controlled value
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      field.onChange(''); // Allow empty string
                    } else {
                      const numberValue = parseFloat(value);
                      if (!isNaN(numberValue)) {
                        field.onChange(numberValue);
                      }
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          key="enable_standard_vessel_filter"
          control={form.control}
          name="enable_standard_vessel_filter"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {messages.settingsReportEnableStandardVesselFilterLabel}
                </FormLabel>
                <FormDescription>
                  {messages.settingsReportEnableStandardVesselFilterDescription}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          key="create_report_default_template"
          control={form.control}
          name="create_report_default_template"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  Create Report Default Template
                </FormLabel>
                <FormDescription>
                  Choose the default template mode when creating new reports.
                  Wizard provides step-by-step guidance, while Quick allows
                  immediate creation with all options.
                </FormDescription>
              </div>
              <FormControl>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wizard">Wizard</SelectItem>
                    <SelectItem value="quick">Quick</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </Form>
  );
};
